import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Lock } from "lucide-react";

const STATUS_COLORS = {
  "Open": "var(--ff-red)",
  "Closed": "var(--ff-text-dim)",
  "Under Review": "var(--ff-yellow)",
};

function CensorableField({ value, label, isCensored, testId }) {
  if (isCensored && value && value !== "[REDACTED]") {
    return (
      <div className="flex items-center gap-2">
        <Lock size={12} className="text-[var(--ff-text-dim)]" />
        <span className="redact" data-testid={testId}>{"█".repeat(Math.min(value.length, 24))}</span>
      </div>
    );
  }
  if (value === "[REDACTED]") {
    return (
      <div className="flex items-center gap-2">
        <Lock size={12} className="text-[var(--ff-text-dim)]" />
        <span className="text-[var(--ff-text-dim)] italic font-mono-ui text-xs" data-testid={testId}>[REDACTED]</span>
      </div>
    );
  }
  return <span data-testid={testId}>{value}</span>;
}

export default function HRInvestigations() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/hr-cases");
      setCases(data);
    })();
  }, [user]);

  const isElevated = user && (user.role === "admin" || user.role === "employee");

  return (
    <div data-testid="hr-page" className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24">
      <div className="label-eyebrow mb-6 text-[var(--ff-red)]">▮ HR / PUBLIC DISCLOSURES</div>
      <h1 className="font-display text-5xl lg:text-7xl mb-4">HR INVESTIGATIONS</h1>
      <p className="text-[var(--ff-text-dim)] text-lg max-w-3xl mb-12">
        Public disclosure log of HR investigation cases. Sensitive personal information is redacted unless you are signed in as an employee or admin.
      </p>

      {!user && (
        <div className="surface-card p-4 mb-8 flex items-center gap-3 text-sm border-l-4 border-l-[var(--ff-yellow)]" data-testid="public-view-banner">
          <Lock size={16} className="text-[var(--ff-yellow)]" />
          <span className="text-[var(--ff-text-dim)]">You're viewing the public log. Certain fields are redacted for privacy. <a href="/login" className="text-[var(--ff-red)]">Sign in</a> to view full case details.</span>
        </div>
      )}

      <div className="space-y-px bg-[var(--ff-border)]">
        <div className="grid grid-cols-12 gap-4 bg-[var(--ff-surface)] p-4 label-eyebrow">
          <div className="col-span-2">Case #</div>
          <div className="col-span-2">Emp. ID</div>
          <div className="col-span-2">Employee</div>
          <div className="col-span-2">Investigator</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Date</div>
        </div>
        {cases.length === 0 && (
          <div className="bg-[var(--ff-bg)] p-8 text-[var(--ff-text-dim)] italic">No cases on record.</div>
        )}
        {cases.map((c) => {
          const censored = c.censored_fields || [];
          return (
            <details key={c.id} className="bg-[var(--ff-bg)] group" data-testid={`hr-case-${c.id}`}>
              <summary className="grid grid-cols-12 gap-4 p-4 cursor-pointer hover:bg-[var(--ff-surface)] text-sm items-center">
                <div className="col-span-2 font-mono-ui">{c.case_number}</div>
                <div className="col-span-2 font-mono-ui text-xs">
                  <CensorableField value={c.employee_id} isCensored={!isElevated && censored.includes("employee_id")} testId={`case-empid-${c.id}`} />
                </div>
                <div className="col-span-2">
                  <CensorableField value={c.employee_name} isCensored={!isElevated && censored.includes("employee_name")} testId={`case-empname-${c.id}`} />
                </div>
                <div className="col-span-2 text-[var(--ff-text-dim)]">
                  <CensorableField value={c.investigator} isCensored={!isElevated && censored.includes("investigator")} testId={`case-inv-${c.id}`} />
                </div>
                <div className="col-span-2">
                  <span className="font-mono-ui text-xs uppercase tracking-[0.18em] px-2 py-1 border" style={{ color: STATUS_COLORS[c.status], borderColor: STATUS_COLORS[c.status] }}>
                    {c.status}
                  </span>
                </div>
                <div className="col-span-2 font-mono-ui text-xs text-[var(--ff-text-dim)]">{c.date_opened}</div>
              </summary>
              <div className="p-6 border-t border-[var(--ff-border)] grid md:grid-cols-2 gap-6">
                <div>
                  <div className="label-eyebrow mb-2">Description</div>
                  <div className="text-sm text-[var(--ff-text-dim)]">
                    <CensorableField value={c.description} isCensored={!isElevated && censored.includes("description")} testId={`case-desc-${c.id}`} />
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow mb-2">Findings</div>
                  <div className="text-sm text-[var(--ff-text-dim)]">
                    <CensorableField value={c.findings} isCensored={!isElevated && censored.includes("findings")} testId={`case-find-${c.id}`} />
                  </div>
                </div>
                {user?.role === "admin" && (
                  <div className="md:col-span-2 border-t border-[var(--ff-border)] pt-4">
                    <div className="label-eyebrow mb-2 text-[var(--ff-red)]">▮ ADMIN ONLY — Private Notes</div>
                    <div className="text-sm">{c.private_notes || "(none)"}</div>
                  </div>
                )}
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
}
